/**
 * Highlighting the search terms inside whatever the reader just opened.
 *
 * The requirement is "show me WHERE it matched, and take me to the next one",
 * on two very different targets: a transaction editor (a React form of inputs
 * and cells) and a document (a repository viewer). Both are ordinary DOM by
 * the time they are on screen, so this works on the DOM rather than on either
 * component's internals — no target has to know it is being searched.
 *
 * **It does not mutate the DOM.** The primary path is the CSS Custom Highlight
 * API: `Range` objects handed to `CSS.highlights`, painted by `::highlight()`.
 * That matters more here than anywhere else in this package, because the
 * obvious implementation — wrapping matches in `<mark>` — edits nodes React
 * owns, and React's next reconciliation either throws them away or, worse,
 * reuses them and corrupts the tree. Ranges are invisible to React entirely.
 *
 * The fallback, for a browser without the API, is an ATTRIBUTE on the element
 * containing each match (`data-twasta-search-hit`) plus injected CSS. It marks
 * the field rather than the words — a real downgrade, stated rather than
 * hidden — and it is cleaned off on teardown. It sets no attribute React set,
 * so a re-render simply drops the marker and the next scan puts it back.
 *
 * Values inside `<input>`/`<textarea>` are NOT text nodes and cannot carry a
 * Range, so those are matched on `value` and marked with the same attribute on
 * both paths. That is why a transaction editor highlights at field level while
 * a document highlights at word level: the browser gives us nothing finer for
 * a form control.
 */

const STYLE_ID = 'twasta-search-highlight-styles'
const HIGHLIGHT_NAME = 'twasta-search'
const ACTIVE_NAME = 'twasta-search-active'

/** Marker attribute for the fallback path and for form controls. */
export const HIT_ATTR = 'data-twasta-search-hit'

/** Elements that are search UI, not searchable content. */
export const UI_ATTR = 'data-twasta-search-ui'

const MIN_TERM_LEN = 2

/** One located occurrence. `range` is absent for a form-control match. */
export interface Occurrence {
  /** The element to scroll to and to mark. */
  element: Element
  range: Range | null
}

interface HighlightRegistryLike {
  set(name: string, highlight: unknown): void
  delete(name: string): void
}

function registry(): HighlightRegistryLike | null {
  const css = (globalThis as unknown as { CSS?: { highlights?: HighlightRegistryLike } }).CSS
  return css?.highlights ?? null
}

/** Whether the browser can paint word-level highlights. */
export function highlightSupported(): boolean {
  return registry() !== null
    && typeof (globalThis as unknown as { Highlight?: unknown }).Highlight === 'function'
}

/**
 * Inject the highlight CSS once.
 *
 * `::highlight()` rules are inert in a browser without the API, and the
 * fallback rules key off an attribute nothing else sets, so both halves can
 * ship unconditionally.
 */
export function ensureHighlightStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
::highlight(${HIGHLIGHT_NAME}) {
  background-color: #fde68a;
  color: #111827;
}
::highlight(${ACTIVE_NAME}) {
  background-color: #f59e0b;
  color: #111827;
}
[${HIT_ATTR}] {
  outline: 2px solid #fbbf24;
  outline-offset: 1px;
  border-radius: 3px;
}
[${HIT_ATTR}="active"] {
  outline: 2px solid #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.28);
}
@media (prefers-reduced-motion: no-preference) {
  [${HIT_ATTR}="active"] { transition: box-shadow 120ms ease-out; }
}
`
  document.head.appendChild(style)
}

/** Case-insensitive `[start, end)` spans of any term, non-overlapping. */
export function findSpans(text: string, terms: string[]): Array<[number, number]> {
  if (!text || !terms.length) return []
  const lowered = text.toLowerCase()
  const claimed: Array<[number, number]> = []
  const free = (start: number, end: number) =>
    claimed.every(([s, e]) => end <= s || start >= e)
  // Longest first, so a multi-word phrase claims its text before the single
  // words inside it can fragment the same match into three.
  for (const term of [...terms].sort((a, b) => b.length - a.length)) {
    const needle = term.toLowerCase()
    if (needle.length < MIN_TERM_LEN) continue
    let at = lowered.indexOf(needle)
    while (at !== -1) {
      const end = at + needle.length
      if (free(at, end)) claimed.push([at, end])
      at = lowered.indexOf(needle, at + 1)
    }
  }
  return claimed.sort((a, b) => a[0] - b[0])
}

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'SVG', 'CANVAS'])

function isSearchable(node: Node): boolean {
  let el: Element | null = node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return false
    // The search panel and the match bar are chrome ABOUT the search, and
    // highlighting our own result list would be both wrong and infinite.
    if (el.hasAttribute(UI_ATTR)) return false
    if (el.getAttribute('aria-hidden') === 'true') return false
    el = el.parentElement
  }
  return true
}

/**
 * Every occurrence of `terms` under `root`, in document order.
 *
 * Text nodes come back as Ranges; `<input>`/`<textarea>`/`<select>` values, and
 * any element whose text is only reachable through a `value`, come back as an
 * element with no Range.
 */
export function collectOccurrences(root: ParentNode, terms: string[]): Occurrence[] {
  if (typeof document === 'undefined' || !terms.length) return []
  const clean = terms.filter((t) => t && t.length >= MIN_TERM_LEN)
  if (!clean.length) return []
  const out: Occurrence[] = []
  const seenControls = new Set<Element>()

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      const text = node.nodeValue || ''
      if (!text.trim()) return NodeFilter.FILTER_REJECT
      return isSearchable(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT
    },
  })
  let node = walker.nextNode()
  while (node) {
    const text = node.nodeValue || ''
    for (const [start, end] of findSpans(text, clean)) {
      const range = document.createRange()
      try {
        range.setStart(node, start)
        range.setEnd(node, end)
      } catch {
        continue // the tree moved under us; the next scan will catch it
      }
      const element = (node.parentElement as Element | null)
      if (element) out.push({ element, range })
    }
    node = walker.nextNode()
  }

  // Form controls: a value is not a text node, so the browser gives us no
  // Range to paint. Mark the control instead — see the module note.
  const controls = (root as ParentNode & { querySelectorAll: ParentNode['querySelectorAll'] })
    .querySelectorAll('input, textarea')
  controls.forEach((control) => {
    if (seenControls.has(control)) return
    if (!isSearchable(control)) return
    const el = control as HTMLInputElement | HTMLTextAreaElement
    if (el.type === 'password' || el.type === 'hidden') return
    if (!findSpans(el.value || '', clean).length) return
    seenControls.add(control)
    out.push({ element: control, range: null })
  })

  return out
}

/**
 * Paint `occurrences`, with `activeIndex` shown as the current one.
 *
 * Returns whether word-level painting was available, so the caller can tell
 * the reader when it is looking at field-level marks instead.
 */
export function applyHighlights(occurrences: Occurrence[], activeIndex: number): boolean {
  clearMarkers()
  const reg = registry()
  const HighlightCtor = (globalThis as unknown as {
    Highlight?: new (...ranges: Range[]) => unknown
  }).Highlight

  const ranged = occurrences.filter((o) => o.range) as Array<Occurrence & { range: Range }>
  const supported = !!reg && typeof HighlightCtor === 'function'

  if (supported && reg && HighlightCtor) {
    const active = occurrences[activeIndex]
    const rest = ranged.filter((o) => o !== active).map((o) => o.range)
    try {
      if (rest.length) reg.set(HIGHLIGHT_NAME, new HighlightCtor(...rest))
      else reg.delete(HIGHLIGHT_NAME)
      if (active?.range) reg.set(ACTIVE_NAME, new HighlightCtor(active.range))
      else reg.delete(ACTIVE_NAME)
    } catch {
      // A stale Range (its node was removed between scan and paint) throws
      // rather than being ignored. Drop the pass; the observer re-scans.
      reg.delete(HIGHLIGHT_NAME)
      reg.delete(ACTIVE_NAME)
    }
  }

  // Elements are marked on BOTH paths: without the API they are the whole
  // highlight, and with it they are how a form control — which can carry no
  // Range — still shows that it matched.
  occurrences.forEach((occurrence, index) => {
    if (supported && occurrence.range) return
    occurrence.element.setAttribute(HIT_ATTR, index === activeIndex ? 'active' : '')
  })
  const active = occurrences[activeIndex]
  if (active && !(supported && active.range)) {
    active.element.setAttribute(HIT_ATTR, 'active')
  }
  return supported
}

/** Remove every marker attribute this module set. */
export function clearMarkers(): void {
  if (typeof document === 'undefined') return
  document.querySelectorAll(`[${HIT_ATTR}]`).forEach((el) => {
    el.removeAttribute(HIT_ATTR)
  })
}

/** Remove every highlight and marker. Safe to call when none are set. */
export function clearHighlights(): void {
  const reg = registry()
  if (reg) {
    reg.delete(HIGHLIGHT_NAME)
    reg.delete(ACTIVE_NAME)
  }
  clearMarkers()
}

/** Bring one occurrence into view, centred where the browser allows it. */
export function scrollToOccurrence(occurrence: Occurrence | undefined): void {
  if (!occurrence) return
  const target = occurrence.element
  try {
    target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
  } catch {
    try { target.scrollIntoView() } catch { /* detached — the next scan re-finds it */ }
  }
}
